import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaChevronLeft, FaChevronRight, FaTrash, FaBalanceScale } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const ArtComparisonScroller = ({ activities, onActivityClick, onDeleteActivity }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullActivities, setFullActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFullActivities = async () => {
      setLoading(true);
      try {
        const loaded = await Promise.all(
          activities.map(async (activity) => {
            try {
              const docRef = doc(db, 'userActivities', activity.id);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
              }
              return activity;
            } catch (error) {
              console.error('Error loading activity:', error);
              return activity;
            }
          })
        );
        setFullActivities(loaded);
      } catch (error) {
        console.error('Error loading activities:', error);
        setFullActivities(activities);
      } finally {
        setLoading(false);
      }
    };

    if (activities.length > 0) {
      loadFullActivities();
    } else {
      setFullActivities([]);
      setLoading(false);
    }
  }, [activities]);

  const currentActivity = fullActivities[currentIndex];
  const hasNext = currentIndex < fullActivities.length - 1;
  const hasPrev = currentIndex > 0;

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (hasPrev) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleDelete = (e, activity) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this art comparison?')) {
      onDeleteActivity(activity);
      // Adjust index if needed
      if (currentIndex >= fullActivities.length - 1 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (fullActivities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FaBalanceScale className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No art comparisons to display</p>
      </div>
    );
  }

  const artwork1 = currentActivity?.metadata?.artwork1 || {};
  const artwork2 = currentActivity?.metadata?.artwork2 || {};
  const comparisonResult = currentActivity?.metadata?.comparisonResult || {};

  return (
    <div className="relative">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePrev}
          disabled={!hasPrev}
          className={`p-2 rounded-lg transition-all ${
            hasPrev
              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
              : 'bg-gray-100 text-gray-300 cursor-not-allowed'
          }`}
        >
          <FaChevronLeft className="w-5 h-5" />
        </button>

        <div className="text-center">
          <span className="text-sm text-gray-600">
            {currentIndex + 1} of {fullActivities.length}
          </span>
          <p className="text-xs text-gray-500 mt-1">
            {artwork1.title || 'Artwork 1'} vs {artwork2.title || 'Artwork 2'}
          </p>
        </div>

        <button
          onClick={handleNext}
          disabled={!hasNext}
          className={`p-2 rounded-lg transition-all ${
            hasNext
              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
              : 'bg-gray-100 text-gray-300 cursor-not-allowed'
          }`}
        >
          <FaChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Activity Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentActivity.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-lg border-2 border-amber-200 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => onActivityClick(currentActivity)}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h4 className="font-bold text-lg text-gray-800 mb-1">Art Comparison</h4>
              <p className="text-sm text-gray-600">
                {artwork1.title || 'Artwork 1'} vs {artwork2.title || 'Artwork 2'}
              </p>
            </div>
            <button
              onClick={(e) => handleDelete(e, currentActivity)}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete comparison"
            >
              <FaTrash className="w-4 h-4" />
            </button>
          </div>

          {/* Artwork Images Side by Side */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {artwork1.imageUrl && (
              <div className="rounded-lg overflow-hidden border-2 border-amber-200">
                <img
                  src={artwork1.imageUrl}
                  alt={artwork1.title || 'Artwork 1'}
                  className="w-full h-32 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div className="p-2 bg-amber-50">
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {artwork1.title || 'Artwork 1'}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {artwork1.artist || 'Unknown Artist'}
                  </p>
                </div>
              </div>
            )}
            {artwork2.imageUrl && (
              <div className="rounded-lg overflow-hidden border-2 border-amber-200">
                <img
                  src={artwork2.imageUrl}
                  alt={artwork2.title || 'Artwork 2'}
                  className="w-full h-32 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div className="p-2 bg-amber-50">
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {artwork2.title || 'Artwork 2'}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {artwork2.artist || 'Unknown Artist'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Comparison Preview */}
          {comparisonResult.insight && (
            <div className="text-sm text-gray-700 line-clamp-3 mb-2">
              {comparisonResult.insight}
            </div>
          )}

          {(comparisonResult.similarities?.length > 0 || comparisonResult.differences?.length > 0) && (
            <div className="flex gap-4 text-xs text-amber-600 font-medium">
              {comparisonResult.similarities?.length > 0 && (
                <span>{comparisonResult.similarities.length} similarities</span>
              )}
              {comparisonResult.differences?.length > 0 && (
                <span>{comparisonResult.differences.length} differences</span>
              )}
            </div>
          )}

          {/* Date */}
          {currentActivity.createdAt && (() => {
            let date;
            try {
              if (currentActivity.createdAt?.toDate) {
                date = currentActivity.createdAt.toDate();
              } else if (currentActivity.createdAt?._seconds) {
                date = new Date(currentActivity.createdAt._seconds * 1000);
              } else if (typeof currentActivity.createdAt === 'number') {
                date = new Date(currentActivity.createdAt);
              } else if (typeof currentActivity.createdAt === 'string') {
                date = new Date(currentActivity.createdAt);
              } else {
                date = new Date(currentActivity.createdAt);
              }
              
              // Check if date is valid
              if (isNaN(date.getTime())) {
                return null;
              }
              
              return (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    {date.toLocaleDateString()}
                  </p>
                </div>
              );
            } catch (error) {
              console.error('Error formatting date:', error);
              return null;
            }
          })()}
        </motion.div>
      </AnimatePresence>

      {/* Dots indicator */}
      {fullActivities.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {fullActivities.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'bg-amber-600 w-6' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArtComparisonScroller;

